import {
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { ManageBackupsInfo } from "IF/interfaceType";
import { useThemeMode } from "Contexts/ThemeContext";

interface ManageBackupsTableProps {
  data: ManageBackupsInfo[];
  tableBodyHeight: number;
  tableBodyWidth: number;
  handleCheckboxChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    id: number
  ) => void;
  handleCheckAll: (checkedData: ManageBackupsInfo[], checked: boolean) => void;
}

export const ManageBackupsTable: React.FC<ManageBackupsTableProps> = ({
  data,
  tableBodyHeight,
  handleCheckboxChange,
  handleCheckAll,
}) => {
  // 各列の幅割合
  const columnWidths: Record<string, string> = {
    checkBox: "10%",
    companyCode: "15%",
    companyName: "20%",
    fileCreatedAt: "20%",
    fileName: "25%",
    fileSize: "10%",
  };

  // フィルター
  const [filterCompanyCode, setFilterCompanyCode] = useState("");
  const [filterCompanyName, setFilterCompanyName] = useState("");
  const [createdAtBefore, setCreatedAtBefore] = useState(
    new Date().toISOString().slice(0, 10)
  );

  function isNullOrUndefined<T>(
    value: T | null | undefined
  ): value is null | undefined {
    return value === null || value === undefined || value === "";
  }

  const addDays = (date: Date, days: number): Date => {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    nextDay.setHours(0, 0, 0, 0);
    return nextDay;
  };

  const filteredData = data?.filter((row) => {
    let codeMatch: boolean;
    if (isNullOrUndefined(filterCompanyCode)) {
      // filterCompanyCodeがnull, undefined, または空文字列の場合
      codeMatch = true;
    } else {
      // filterCompanyCodeが値を持つ場合
      // rowCompanyCodeがnull, undefined, または空文字列でない、かつ includesがtrueの場合にcodeMatchをtrueにする
      codeMatch =
        !isNullOrUndefined(row.companyCode) &&
        row.companyCode.includes(filterCompanyCode);
    }

    let nameMatch: boolean;
    if (isNullOrUndefined(filterCompanyName)) {
      nameMatch = true;
    } else {
      nameMatch =
        !isNullOrUndefined(row.companyName) &&
        row.companyName.includes(filterCompanyName);
    }

    const dateMatch = createdAtBefore
      ? new Date(row.createdAt) < addDays(new Date(createdAtBefore), 1)
      : true;
    return codeMatch && nameMatch && dateMatch;
  });

  //ヘッダチェックボックス状態
  const isAllChecked = filteredData?.every((row) => row.check) ?? false;
  const isSomeChecked = filteredData?.some((row) => row.check) ?? false;

  const theme = useTheme();
  const { mode } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      {isMobile ? (
        // 📱 カード表示
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              mx: 1,
            }}
          >
            {/* ✅ すべてチェック */}
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={isAllChecked}
                  indeterminate={isSomeChecked && !isAllChecked}
                  onChange={(e) =>
                    handleCheckAll(filteredData, e.target.checked)
                  }
                />
              }
              label="すべて選択"
            />

            {/* ✅ 日付フィルター */}
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                作成日
              </Typography>
              <TextField
                type="date"
                value={createdAtBefore}
                onChange={(e) => setCreatedAtBefore(e.target.value)}
                size="small"
                sx={{
                  backgroundColor: theme.palette.custom.sForm,
                  borderRadius: 1,
                  minWidth: 100,
                  input: {
                    fontSize: "0.75rem",
                    padding: "4px",
                    ...(mode === "dark" && {
                      "&::-webkit-calendar-picker-indicator": {
                        filter: "invert(1)",
                      },
                    }),
                  },
                }}
              />
              <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                まで表示
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              minHeight: tableBodyHeight - 45,
              maxHeight: tableBodyHeight - 45,
              overflowY: "auto",
            }}
          >
            {filteredData.map((row, index) => (
              <Paper
                key={index}
                sx={{
                  p: 2,
                  mb: 1,
                  mx: 1,
                  backgroundColor: theme.palette.custom.row,
                }}
              >
                <Box display="flex" alignItems="center" mb={1} gap={1}>
                  <Checkbox
                    checked={row.check}
                    onChange={(event) => handleCheckboxChange(event, row.id)}
                  />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {row.fileName}
                  </Typography>
                </Box>
                <Typography variant="body2">
                  企業コード: {row.companyCode}
                </Typography>
                <Typography variant="body2">
                  企業名: {row.companyName}
                </Typography>
                <Typography variant="body2">作成日: {row.createdAt}</Typography>
                <Typography variant="body2">サイズ: {row.fileSize}</Typography>
              </Paper>
            ))}
          </Box>
        </>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            minHeight: tableBodyHeight,
            maxHeight: tableBodyHeight,
            backgroundColor: theme.palette.custom.row,
            overflowY: "auto",
          }}
        >
          <Table
            sx={{
              "& .MuiTable-root": {
                fontSize: "0.8rem",
              },
            }}
          >
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                backgroundColor: theme.palette.custom.tableHeader,
                zIndex: 1,
                color: "white",
              }}
            >
              <TableRow
                sx={{
                  "& > th": {
                    borderBottom: "none",
                    padding: "4px 8px 0 8px",
                    marginBottom: 0,
                    height: "28px",
                  },
                }}
              >
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.checkBox,
                    color: "white",
                  }}
                >
                  <Typography fontWeight="fontWeightBold">Check</Typography>
                </TableCell>
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.companyCode,
                    color: "white",
                    display: { xs: "none", lg: "table-cell" },
                  }}
                >
                  <Typography fontWeight="fontWeightBold">
                    企業コード
                  </Typography>
                </TableCell>
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.companyName,
                    color: "white",
                    display: { xs: "none", lg: "table-cell" },
                  }}
                >
                  <Typography fontWeight="fontWeightBold">企業名</Typography>
                </TableCell>
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.fileCreatedAt,
                    color: "white",
                  }}
                >
                  <Typography fontWeight="fontWeightBold">作成日</Typography>
                </TableCell>
                <TableCell
                  rowSpan={2}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.fileName,
                    color: "white",
                  }}
                >
                  <Typography fontWeight="fontWeightBold">
                    ファイル名
                  </Typography>
                </TableCell>
                <TableCell
                  rowSpan={2}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.fileSize,
                    color: "white",
                    display: { xs: "none", sm: "table-cell" },
                  }}
                >
                  <Typography fontWeight="fontWeightBold">サイズ</Typography>
                </TableCell>
              </TableRow>
              <TableRow
                sx={{
                  "& > th": {
                    borderBottom: "none",
                    padding: "0px",
                    height: "28px",
                  },
                  marginTop: "-10px",
                }}
              >
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.checkBox,
                    color: "white",
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAllChecked}
                    indeterminate={isSomeChecked && !isAllChecked}
                    onChange={(e) =>
                      handleCheckAll(filteredData, e.target.checked)
                    }
                    color="default"
                    aria-label="チェックボックス"
                  />
                </TableCell>
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.companyCode,
                    color: "white",
                    display: { xs: "none", lg: "table-cell" },
                  }}
                >
                  <TextField
                    variant="standard"
                    value={filterCompanyCode}
                    onChange={(e) =>
                      setFilterCompanyCode(e.target.value.toUpperCase())
                    }
                    placeholder="フィルター"
                    sx={{
                      backgroundColor: theme.palette.custom.sForm,
                      borderRadius: 1,
                      width: "70%",
                      input: {
                        fontSize: "0.75rem",
                        padding: "4px",
                      },
                    }}
                    inputProps={{
                      style: { fontSize: "0.75rem" },
                    }}
                  />
                </TableCell>
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.companyName,
                    color: "white",
                    display: { xs: "none", lg: "table-cell" },
                  }}
                >
                  <TextField
                    variant="standard"
                    value={filterCompanyName}
                    onChange={(e) => setFilterCompanyName(e.target.value)}
                    placeholder="フィルター"
                    sx={{
                      backgroundColor: theme.palette.custom.sForm,
                      borderRadius: 1,
                      width: "70%",
                      input: {
                        fontSize: "0.75rem",
                        padding: "4px",
                      },
                    }}
                    inputProps={{
                      style: { fontSize: "0.75rem" },
                    }}
                  />
                </TableCell>
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.createdAt,
                    minWidth: 210,
                    color: "white",
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.8rem", mr: 1 }}
                    >
                      ～
                    </Typography>
                    <TextField
                      variant="standard"
                      type="date"
                      value={createdAtBefore}
                      onChange={(e) => setCreatedAtBefore(e.target.value)}
                      sx={{
                        backgroundColor: theme.palette.custom.sForm,
                        borderRadius: 1,
                        width: "50%",
                        input: {
                          fontSize: "0.75rem",
                          padding: "4px",
                          ...(mode === "dark" && {
                            "&::-webkit-calendar-picker-indicator": {
                              filter: "invert(1)",
                            },
                          }),
                        },
                      }}
                      inputProps={{
                        style: { fontSize: "0.75rem" },
                      }}
                      size="small"
                      fullWidth
                    />
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData?.map((row, index) => (
                <TableRow key={index}>
                  <TableCell
                    sx={{
                      width: "10%",
                      textAlign: "center",
                    }}
                    size={"small"}
                  >
                    <Checkbox
                      checked={row.check}
                      onChange={(event) => handleCheckboxChange(event, row.id)}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      width: columnWidths.companyCode,
                      display: { xs: "none", lg: "table-cell" },
                    }}
                  >
                    {row.companyCode}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      width: columnWidths.companyName,
                      display: { xs: "none", lg: "table-cell" },
                    }}
                  >
                    {row.companyName}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      width: columnWidths.fileCreatedAt,
                    }}
                  >
                    {row.createdAt}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      width: columnWidths.fileName,
                    }}
                  >
                    {row.fileName}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      width: columnWidths.size,
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    {row.fileSize}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};
export default ManageBackupsTable;
